import { FunctionComponent, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { faChevronRight, faPlus, faSearch, faShuffle } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components/Icon/Icon";
import { classNames } from "@/utils/classNames";

import { normalizeSpintaxSearch, SpintaxRow } from "../utils/spintaxRows";

import { SpintaxSectionRow } from "./SpintaxSectionRow";

interface SpintaxSectionCardProps {
	rows: SpintaxRow[];
	inUseCount: number;
	onRowInsert: (row: SpintaxRow) => void;
	onRowConfigure: (row: SpintaxRow) => void;
	onCreateNew: () => void;
}

/**
 * Collapsible "Spintax" section card: header with in-use count and a
 * "Create new" action, body with a search input and one row per RANDOM_VALUE
 * column of the list. Collapsed by default.
 */
export const SpintaxSectionCard: FunctionComponent<SpintaxSectionCardProps> = ({
	rows,
	inUseCount,
	onRowInsert,
	onRowConfigure,
	onCreateNew,
}) => {
	const { t } = useTranslation();

	const [isOpen, setIsOpen] = useState<boolean>(false);
	const [search, setSearch] = useState<string>("");

	const filteredRows = useMemo(() => {
		const query = normalizeSpintaxSearch(search);

		if (!query) {
			return rows;
		}

		return rows.filter((row) => normalizeSpintaxSearch(row.name).includes(query));
	}, [rows, search]);

	return (
		<div className="w-full border border-border-light rounded-lg bg-white overflow-hidden">
			<div
				className={classNames(
					"flex items-center justify-between gap-2 px-3 py-2.5 bg-background-light/60 cursor-pointer select-none",
					isOpen && "border-b border-border-light"
				)}
				onClick={() => setIsOpen((open) => !open)}
			>
				<div className="flex items-center gap-2 min-w-0 flex-1">
					<Icon
						className={classNames(
							"w-3.5 h-3.5 flex-shrink-0 text-text-meta transition-transform",
							isOpen && "rotate-90"
						)}
						icon={faChevronRight}
					/>
					<span className="w-[22px] h-[22px] flex-shrink-0 rounded bg-spintax-light text-spintax flex items-center justify-center">
						<Icon className="w-3 h-3" icon={faShuffle} />
					</span>
					<span className="text-xs font-semibold text-text whitespace-nowrap">
						{t("spintax.section.title")}
					</span>
					<span className="text-xxs font-medium px-2 py-0.5 bg-white border border-border-light rounded-full text-text-meta whitespace-nowrap">
						{t("spintax.section.inUse", { count: inUseCount })}
					</span>
				</div>
				<button
					className="inline-flex items-center gap-1 px-2 py-1 flex-shrink-0 bg-white border border-border-light rounded-md text-xs font-medium text-text-meta cursor-pointer hover:bg-background-light"
					type="button"
					onClick={(e) => {
						e.stopPropagation();
						onCreateNew();
					}}
				>
					<Icon className="w-3 h-3" icon={faPlus} />
					{t("spintax.section.createNew")}
				</button>
			</div>

			{isOpen && (
				<div className="flex flex-col overflow-hidden">
					<div className="relative p-2 border-b border-border-light bg-white flex-shrink-0">
						<Icon
							className="absolute left-[18px] top-1/2 -translate-y-1/2 w-3 h-3 text-text-meta"
							icon={faSearch}
						/>
						<input
							className="w-full pl-7 pr-2.5 py-1.5 border border-border-light rounded-md text-xs bg-background-light focus:outline-none focus:bg-white focus:border-blue-action"
							placeholder={t("spintax.section.searchPlaceholder")}
							type="text"
							value={search}
							onChange={(e) => setSearch(e.target.value)}
						/>
					</div>

					<div className="overflow-y-auto max-h-[280px] p-2 flex flex-col gap-1">
						{filteredRows.length === 0 && (
							<div className="px-2 py-2 text-xs text-text-meta italic">
								{t("spintax.section.emptySearch")}
							</div>
						)}

						{filteredRows.map((row) => (
							<SpintaxSectionRow
								key={row.key}
								row={row}
								onConfigure={onRowConfigure}
								onInsert={onRowInsert}
							/>
						))}
					</div>
				</div>
			)}
		</div>
	);
};
